import { useEffect, useState } from "react";
import { UserRole } from "@repo/database";
import { formatIntToCurrency, groupBy } from "@repo/utils";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import type { ReceiptReport } from "~/managers/use-register-manager";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";
import { useRegisterManager } from "~/managers/use-register-manager";
import { useAuthStore } from "~/stores/use-auth-store";
import { useRegisterStore } from "~/stores/use-register-store";
import { RegisterButton } from "../ui/register-button";

export default function EndDayScreen() {
  const { t } = useTranslation("common");
  const [periodStart, setPeriodStart] = useState<Date | undefined>();

  const [reportId, setReportId] = useState<string | undefined>();
  const impersonateId = useAuthStore((state) => state.impersonateId);
  const [isEndingDay, setIsEndingDay] = useState(false);

  const [groupedReports, setGroupedReports] =
    useState<Record<string, ReceiptReport["data"][number]["payments"]>>();

  const { printClosingReport, endDay } = useRegisterManager();

  const { setEndDayOpen } = useRegisterStore();

  const { data, isFetching: fetchingData } =
    api.report.getReceiptReports.useQuery(
      {},
      {
        staleTime: 0,
      },
    );

  const { data: report, isFetching: isFetchingReport } =
    api.report.getClosingReportById.useQuery(reportId, {
      enabled: !!reportId,
    });

  const handleEndDay = async () => {
    setIsEndingDay(true);
    const result = await endDay(periodStart);

    if (result) {
      setReportId(result);
    }
  };

  useEffect(() => {
    if (report && reportId) {
      printClosingReport(report);
      setReportId(false);
      // Do not remove, will cause infinite loop. Need to find a better solution.
      setTimeout(() => {
        setEndDayOpen(false);
      }, 150);
    }
  }, [printClosingReport, report, setEndDayOpen, reportId]);

  useEffect(() => {
    if (data) {
      const payments = data.data.map((d) => d.payments).flat();
      setGroupedReports(groupBy(payments, "name"));
    }
  }, [data]);

  return (
    <div>
      {impersonateId && (
        <div>
          <input
            className="text-black"
            type="date"
            value={periodStart ? format(periodStart, "yyyy-MM-dd") : ""}
            onChange={(e) => {
              const newDate = e.target.value;
              if (periodStart) {
                const time = format(periodStart, "HH:mm");
                setPeriodStart(new Date(`${newDate}T${time}`));
              } else {
                setPeriodStart(new Date(`${newDate}T00:00`));
              }
            }}
          />
          <input
            className="text-black"
            type="time"
            value={periodStart ? format(periodStart, "HH:mm") : ""}
            onChange={(e) => {
              const newTime = e.target.value;
              if (periodStart) {
                const date = format(periodStart, "yyyy-MM-dd");
                setPeriodStart(new Date(`${date}T${newTime}`));
              } else {
                setPeriodStart(new Date(`1970-01-01T${newTime}`));
              }
            }}
          />
        </div>
      )}
      {fetchingData && <div>{t("fetching_data")}</div>}
      {!fetchingData && !groupedReports && <div>{t("no_data")}</div>}
      {data?.latestReportDate && (
        <div className="border-b-2 p-2 text-center">
          {t("ClosingReport.last_closing_date", {
            date: format(data.latestReportDate, "dd-MM-yyyy HH:mm"),
          })}
        </div>
      )}
      {!fetchingData && groupedReports && (
        <div className="flex flex-col gap-2">
          <div className="border-b py-2 px-2">
            {Object.entries(groupedReports).map(([methodName, payments]) => (
              <div
                className="flex flex-row gap-2 justify-between"
                key={methodName}
              >
                <span>{methodName}</span>
                <span>
                  {formatIntToCurrency(
                    payments.reduce((acc, curr) => acc + curr.amount, 0),
                  )}
                </span>
              </div>
            ))}
          </div>

          {!data || data.data.map((payment) => payment).length === 0 ? (
            <div>{t("no_payments_found")}</div>
          ) : (
            <RegisterButton
              disabled={isEndingDay || isFetchingReport}
              onClick={handleEndDay}
              className="w-full"
              variant="destructive"
            >
              {t("end_day")}
            </RegisterButton>
          )}
        </div>
      )}
    </div>
  );
}
